import React from 'react'
import { MdEditCalendar, MdEventBusy } from 'react-icons/md'
import type { HolidayRange } from '@/utils/holidayRanges'
import { describeRange } from '@/utils/skipRanges'

/**
 * The customer's booked skips.
 *
 * A holiday runs [start, end) where `end` is the RESTART date and is not
 * itself paused. That convention is right for the data and wrong for a
 * customer: nobody describes a break as "the 1st to the 11th" when the last
 * day off is the 10th. So the last SKIPPED day is what gets shown, and the
 * conversion happens here rather than being repeated at every call site.
 *
 * There is one control: adding and editing both open the same calendar (which
 * shows every booked holiday at once), so a per-row Edit would just be a second
 * door to the same room.
 */
interface Props {
  ranges: HolidayRange[]
  /** Open the holiday calendar, where holidays are added and edited together. */
  onManage?: () => void
  disabled?: boolean
  loading?: boolean
}

const SkippedDeliveriesPanel: React.FC<Props> = ({
  ranges,
  onManage,
  disabled = false,
  loading = false,
}) => {
  // Soonest first: a customer checking this wants the next break, not the
  // one they booked most recently.
  const sorted = [...ranges].sort((a, b) => a.start.getTime() - b.start.getTime())

  return (
    <section className="sun-panel sun-skips">
      <div className="sun-card-head">
        <h2 className="sun-card-title">Paused deliveries</h2>
        {onManage && (
          <button
            type="button"
            className="sun-btn sun-btn-add"
            onClick={onManage}
            disabled={disabled}
          >
            <MdEditCalendar aria-hidden="true" /> Add / Edit paused deliveries
          </button>
        )}
      </div>

      {loading ? (
        <div className="sun-loading" role="status">
          <div className="spinner" aria-hidden="true" />
          <p>Loading your paused deliveries...</p>
        </div>
      ) : sorted.length === 0 ? (
        <p className="sun-empty">
          You have no paused deliveries booked.
        </p>
      ) : (
        <ul className="sun-skip-list">
          {sorted.map(range => (
            <li key={range.id} className="sun-skip-row">
              <MdEventBusy className="sun-skip-icon" aria-hidden="true" />
              <span className="sun-skip-dates">{describeRange(range)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export default SkippedDeliveriesPanel
